import { Collection } from "@/types/collection/collection";
import { PortalSettings } from "@/types/portal";

import { Portal, Controllers } from "@infoland/iprova-api";
import Vue from "vue";

export interface PortalInfoDictionary<T> {
    [portalId: number]: T;
}

export interface PortalCollectionState {
    selectedCollectionId?: number;
    expandedCollectionIds?: number[];
}
export interface ActionResult<S, E> {
    success: boolean;
    successData?: S;
    errorData?: E;
}

// state type
interface PortalState {
    portals: Portal[];
    defaultPortal: PortalSettings;
    portalCollections: PortalInfoDictionary<Collection[]> | null;
    collectionStates: PortalInfoDictionary<PortalCollectionState> | null;
}

export function initialize(): PortalState {
    return {
        portals: null,
        defaultPortal: null,
        portalCollections: {},
        collectionStates: {}
    };
}

// initial state
const state: PortalState = initialize();

// getters for this store
const getters = {
    portals(state: PortalState): Portal[] {
        return state.portals;
    },

    defaultPortal(state: PortalState): PortalSettings {
        return state.defaultPortal;
    },

    collections(state: PortalState): PortalInfoDictionary<Collection[]> {
        return state.portalCollections;
    },

    collectionStates(state: PortalState): PortalInfoDictionary<PortalCollectionState> {
        return state.collectionStates;
    }
};

// mutations for this store
const mutations = {
    _portals(state: PortalState, portals: Portal[]): void {
        state.portals = portals;
    },

    _defaulPortal(state: PortalState, defaultPortalSettings: PortalSettings): void {
        state.defaultPortal = defaultPortalSettings;
    },

    _lastVisitedTime(state: PortalState, portal: Portal): void {
        const index = state.portals.findIndex(obj => obj.portal_id == portal.portal_id);

        state.portals[index].last_visited_date_time = portal.last_visited_date_time;
    },

    portalCollections(state: PortalState, data: { portalId: number; collections: Collection[] }) {
        state.portalCollections[data.portalId] = data.collections;
    },

    portalCollectionState(state: PortalState, data: { portalId: number; collectionState: PortalCollectionState }) {
        state.collectionStates[data.portalId] = data.collectionState;
    },

    /**
     * Stores the selected collection for a portal
     * @param state state to update
     * @param data portal ID and the collection ID to set as selected for the portal
     */
    selectedCollection(state: PortalState, data: { portalId: number; collectionId: number }) {
        let portalState: PortalCollectionState = state.collectionStates[data.portalId];
        if (!portalState) {
            portalState = {};
            Vue.set(state.collectionStates, data.portalId, portalState);
        }

        portalState.selectedCollectionId = data.collectionId;
    },

    /**
     * Stores the expanded collections for a portal
     * @param state state to update
     * @param data portal ID and the collection IDs to set as expanded for the portal
     */
    expandedCollections(state: PortalState, data: { portalId: number; collectionIds: number[] }) {
        let portalState: PortalCollectionState = state.collectionStates[data.portalId];
        if (!portalState) {
            portalState = {};
            Vue.set(state.collectionStates, data.portalId, portalState);
        }

        portalState.expandedCollectionIds = data.collectionIds;
    }
};

// actions for this store
const actions = {
    async getPortals(context: { commit: (path: string, value: Portal[]) => void }): Promise<Portal[]> {
        // load user data for current user
        const portals = await Controllers.PortalController.getPortals(false, false);
        context.commit("_portals", portals);
        return portals;
    },

    async savePortal(context: { commit: (path: string, value: Portal[]) => void }, portal): Promise<boolean> {
        const result = await Controllers.PortalController.updatePortal(portal.id, portal.portalProps);
        context.commit("_lastVisitedTime", portal.portalProps);
        return result;
    },

    async loadDefaultPortalSettings(context: { commit: (path: string, value: PortalSettings) => void }): Promise<void> {
        const response = await Controllers.GenericController.getAsync<PortalSettings>("/portals/default_portal_settings");
        context.commit("_defaulPortal", response);
    }
};

// export the namespace
export const namespace = "portal";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
